import { cn } from '../../lib/utils';
import { CalendarDate, CalendarDateTime, parseDate, ZonedDateTime } from '@internationalized/date';
import { cva, VariantProps } from 'class-variance-authority';
import { format, isToday, isValid } from 'date-fns';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import React, { useState } from 'react';
import {Button, Calendar, CalendarCell, CalendarGrid, CalendarGridBody, CalendarGridHeader, CalendarHeaderCell, DateInput as DateInputter, DatePicker, DateSegment, DateValue, Dialog, Group, Heading, Label, Popover} from 'react-aria-components';

const dateGroupVariants = cva(
    "flex items-center justify-between w-full bg-white",
    {
        variants: {
            variant: {
                default: "border",
                inline: ""
            },
            size: {
                default: "",
                sm: ""
            }
        },
        defaultVariants: {
            variant: "default",
            size: "default"
        }
    }
)

const dateInputVariants = cva(
    "flex items-center py-1 text-sm w-full", 
    {
        variants: {
            variant: {
                default: "px-2",
                inline: ""
            },
            size: {
                default: "h-9",
                sm: "h-8"
            }
        },
        defaultVariants: {
            variant: "default",
            size: "default"
        }
    }
)

interface DateInputProps extends VariantProps<typeof dateGroupVariants> {
    date?: Date
    onDateChange?: (date:Date | undefined) => void
    className?: string
    containerClassName?: string
    inputClassName?: string
    buttonClassName?: string
    disabled?: boolean
}

export const DateInput = ({ date:default_date, onDateChange, disabled, variant, size, inputClassName }:DateInputProps) => {
    const [date, setdate] = useState<DateValue | undefined | null>(null);

    React.useEffect(() => {
        if(default_date) {
            if(typeof default_date === 'object'){
                setdate(parseDate(format(default_date, "yyyy-MM-dd")))
            }
        }
    }, [default_date])

    const handleChange = (date:CalendarDate | CalendarDateTime | ZonedDateTime | null) => {
        setdate(date);
        onDateChange && onDateChange(date ? date.toDate("CET") : undefined);
    }

  return (
    <DatePicker
        value={date}
        onChange={handleChange}
        isDisabled={disabled}
        className={cn("",
            disabled && 'cursor-not-allowed opacity-50'
        )}
    >
        <Label className='sr-only'>Date</Label>
        <Group className={cn(dateGroupVariants({ variant, size }), inputClassName)}>
            <DateInputter className={cn(dateInputVariants({ variant, size }), inputClassName)}>
            {(segment) => {
                return (
                <DateSegment 
                    segment={{...segment,
                        text: segment.type === "literal" ? segment.text.replace(/-/g, "/") : segment.text,
                    }} 
                    className={cn("focus:outline-none focus:bg-primary focus:text-primary-foreground px-0.5 rounded-sm caret-transparent",
                        segment.isPlaceholder && "text-muted-foreground",
                        (segment.type === "literal" && !isValid(date?.toDate("CET"))) && "text-muted-foreground"
                    )}
                />
                )
            }}
            </DateInputter>
            <Button className="w-6 h-6 -ml-1 duration-300 text-neutral-400 hover:text-neutral-600"><CalendarIcon className='w-4 h-4'/></Button>
        </Group>
        <Popover className="z-50 p-4 bg-white border shadow-md outline-none rounded-xl text-popover-foreground data-[entering]:animate-in data-[entering]:fade-in data-[exiting]:animate-out data-[exiting]:fade-out">
            <Dialog>
                <Calendar className="text-sm">
                    <header className='flex items-center justify-between gap-4 mb-3'>
                        <Button slot="previous" className="flex items-center justify-center w-6 h-6 border rounded-md"><ChevronLeftIcon className='w-4 h-4'/></Button>
                        <Heading className='text-sm font-medium capitalize'/>
                        <Button slot="next" className="flex items-center justify-center w-6 h-6 border rounded-md"><ChevronRightIcon className='w-4 h-4'/></Button>
                    </header>

                    <CalendarGrid>
                        <CalendarGridHeader>
                            {(day) => <CalendarHeaderCell className="text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]">{day}</CalendarHeaderCell>}
                        </CalendarGridHeader>
                        <CalendarGridBody>
                            {(date) => { 
                                const d = new Date(date.year, date.month-1, date.day)
                                const is_today = isToday(d);
                                const is_next_month = d.getMonth() === new Date().getMonth() + 1;
                                const is_previous_month = d.getMonth() === new Date().getMonth() - 1;
                                return (
                                    <CalendarCell 
                                        date={date} 
                                        className={cn("inline-flex items-center justify-center gap-2 p-0 text-center rounded-md w-9 h-9 hover:bg-accent whitespace-nowrap",
                                            is_today && "!bg-primary !text-primary-foreground",
                                            is_next_month && "opacity-50",
                                            is_previous_month && "opacity-50"
                                        )}
                                    />
                                )
                            }}
                        </CalendarGridBody>
                    </CalendarGrid>
                </Calendar>
            </Dialog>
        </Popover>
    </DatePicker>
    );
};


// import React, { useEffect } from "react";
// import { Popover, PopoverTrigger, PopoverContent } from "./popover";
// import { Input } from "./input";
// import { Button } from "./button";
// import { CalendarIcon } from "lucide-react";
// import { Calendar } from "./calendar";
// import { format, isValid } from "date-fns";
// import { cn } from "@/lib/utils";
// import { useMaskito } from "@maskito/react"
// import { maskitoDateOptionsGenerator, maskitoWithPlaceholder } from '@maskito/kit';
// import type {MaskitoOptions} from '@maskito/core';
// import { newDate } from "@/lib/helpers";

// interface DateInputProps {
//     date?: Date | string
//     onDateChange?: (date:Date | undefined) => void
//     containerClassName?: string
//     inputClassName?: string
//     buttonClassName?: string
//     disabled?: boolean
// }

// export function DateInput({ date:stateDate, onDateChange=()=>{}, containerClassName, inputClassName, buttonClassName, disabled=false }:DateInputProps) {
//     const PLACEHOLDER = 'dd/mm/yyyy';

//     const dateOptions = maskitoDateOptionsGenerator({ 
//         mode: 'dd/mm/yyyy', 
//         separator: '/',
//     });

//     const {
//         plugins, // plugins keeps caret inside actual value and remove placeholder on blur
//         ...placeholderOptions
//         // pass 'true' as second argument to add plugin to hide placeholder when input is not focused
//     } = maskitoWithPlaceholder(PLACEHOLDER, true);

//     const options = {
//         ...dateOptions,
//         plugins: plugins.concat(dateOptions.plugins || []),
//         preprocessors: [
//             // Always put it BEFORE all other preprocessors
//             ...placeholderOptions.preprocessors,
//             ...dateOptions.preprocessors,
//         ],
//         postprocessors: [
//             ...dateOptions.postprocessors,
//             // Always put it AFTER all other postprocessors
//             ...placeholderOptions.postprocessors,
//         ]
//     } satisfies Required<MaskitoOptions>;


//     const maskedInputRef = useMaskito({ options });

//     const [date, setDate] = React.useState<Date | undefined>(stateDate ? newDate(stateDate) : undefined);
//     const [stringDate, setStringDate] = React.useState<string>("");
//     const [errorMessage, setErrorMessage] = React.useState<string>("");

//     React.useEffect(()=>{
//         if (stateDate && isValid(stateDate)) {
//             setStringDate(format(stateDate, "dd/MM/yyyy"));
//         }
//     }, [])

//     useEffect(()=>{
//         if(stateDate && date !== stateDate){
//             setDate(stateDate ? newDate(stateDate) : undefined)
//             setStringDate(format(stateDate, "dd/MM/yyyy"));
//         }
//     }, [stateDate])

//     return (
//         <Popover>
//             <div className={cn("relative w-full", containerClassName)}>
//                 <Input
//                     disabled={disabled}
//                     type="text"
//                     onInput={(e)=>{
//                         const [day, month, year] = e.currentTarget.value.split("/").map(Number);
//                         const parsedDate = new Date(year, month - 1, day);
//                         if(!isValid(parsedDate)){
//                             onDateChange(undefined)
//                         } else {
//                             setErrorMessage("");
//                             setDate(parsedDate);
//                             onDateChange(parsedDate)
                            
//                         }
//                         setStringDate(e.currentTarget.value)
//                     }}
//                     value={stringDate}
//                     onBlur={(e)=>{
//                         const [day, month, year] = e.currentTarget.value.split("/").map(Number);
//                         const parsedDate = new Date(year, month - 1, day);
//                         if(!isValid(parsedDate)){
//                             setErrorMessage("Invalid Date");
//                             setDate(undefined);
//                             onDateChange(undefined)
//                         }
//                     }}
//                     placeholder="dd/mm/yyyy"
//                     ref={maskedInputRef}
//                     className={cn(``, inputClassName)}
//                 />
//                 <PopoverTrigger asChild>
//                     <Button
//                         variant={"outline"}
//                         className={cn(
//                             "font-normal absolute right-0 translate-y-[-50%] top-[50%] rounded-l-none",
//                             !date && "text-muted-foreground",
//                             buttonClassName
//                         )}
//                     >
//                         <CalendarIcon className="w-4 h-4 text-neutral-600" strokeWidth={1.5}/>
//                     </Button>
//                 </PopoverTrigger>
//             </div>
//             <PopoverContent className="w-auto p-0">
//                 <Calendar
//                     mode="single"
//                     selected={date}
//                     onSelect={(selectedDate) => {
//                         if (!selectedDate) return;
//                         setDate(selectedDate);
//                         onDateChange(selectedDate)
//                         setStringDate(format(selectedDate, "dd/MM/yyyy"));
//                         setErrorMessage("");
//                     }}
//                     defaultMonth={date}
//                     initialFocus
//                 />
//             </PopoverContent>
//         </Popover>
//     );
// }